import React, { useState, useEffect } from 'react';
import api from '../services/apiService';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useNavigation } from '../contexts/NavigationContext.js';
import { useLanguage } from '../contexts/LanguageContext.js';

function RegisterForm() {
    const navigate = useNavigate();
    const { isMobile } = useNavigation();
    const { t } = useLanguage();

    // Use location to get URL query parameters
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const invitationToken = queryParams.get('invitation_token');
    const communityId = queryParams.get('community_id');

    const [communityInfo, setCommunityInfo] = useState(null);
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        display_name: '',
        invitation_token: invitationToken || null,
        invitation_community_id: communityId || null
    });

    // Add state for terms acceptance
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [isInvitedRegistration, setIsInvitedRegistration] = useState(false);
    const [emailReadOnly, setEmailReadOnly] = useState(false);

    // Fetch community information and invitation details if invitation parameters are present
    useEffect(() => {
        const fetchInvitationDetails = async () => {
            if (!invitationToken || !communityId) return;

            try {
                // First get community info
                const communityResponse = await api.get(`/communities/${communityId}`);
                setCommunityInfo(communityResponse.data);
                setIsInvitedRegistration(true);

                // Then get invitation details to extract the email
                try {
                    const invitationResponse = await api.get(`/invitations/details?token=${invitationToken}&community_id=${communityId}`);
                    if (invitationResponse.data && invitationResponse.data.email) {
                        // Pre-fill the email field and make it read-only
                        setFormData(prev => ({
                            ...prev,
                            email: invitationResponse.data.email
                        }));
                        setEmailReadOnly(true);
                    }
                } catch (inviteError) {
                    // If we can't get the email, we'll just let the user enter it
                }
            } catch (error) {
                // Don't set error message as this isn't critical for registration
            }
        };

        if (invitationToken && communityId) {
            fetchInvitationDetails();
        }
    }, [invitationToken, communityId]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        // Don't update email if it's read-only
        if (name === 'email' && emailReadOnly) return;
        
        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Check if terms are accepted
        if (!termsAccepted) {
            setErrorMessage(t('auth.register.termsRequired'));
            return;
        }
        
        setLoading(true);
        setErrorMessage('');
        setSuccessMessage('');

        try {
            const response = await api.post("/auth/register", formData);
            
            // Use the message from the server, which now tells users whether their email was
            // auto-verified or if they need to check their email
            setSuccessMessage(response.data.message || t('auth.register.registrationSuccess'));

            setFormData({
                email: '',
                password: '',
                display_name: '',
                invitation_token: invitationToken || null,
                invitation_community_id: communityId || null
            });

            // If the user was registered via invitation (email auto-verified),
            // redirect to login after a short delay
            if (response.data.user && response.data.user.email_verified) {
                setTimeout(() => {
                    navigate('/login');
                }, 3000);
            }
        } catch (error) {
            setErrorMessage(error.response?.data?.error || t('auth.register.registrationFailed'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="container mt-5">
            <div className="text-center mb-4">
                <img 
                    src="/MUSI.ico" 
                    alt="MUSI Logo" 
                    className="img-fluid" 
                    style={{ maxHeight: isMobile ? '90px' : '120px' }} 
                />
                <h2 className="mt-3">{t('auth.register.title')}</h2>
            </div>

            {communityInfo && (
                <div className="alert alert-info mb-4">
                    <h5 className="mb-2">{t('auth.register.invitationReceived')}</h5>
                    <p className="mb-1">{t('auth.register.communityLabel')} <strong>{communityInfo.name}</strong></p>
                    {communityInfo.description && (
                        <p className="mb-0 text-muted">{communityInfo.description}</p>
                    )}
                    <hr />
                    <p className="mb-0">
                        {t('auth.register.completeRegistration')}
                    </p>
                </div>
            )}
            
            <div className="row justify-content-center">
                <div className="col-md-6">
                    <form onSubmit={handleSubmit} className="p-4 border rounded shadow-sm bg-white">
                        {/* Email */}
                        <div className="mb-3">
                            <label htmlFor="email" className="form-label">{t('auth.register.email')}</label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                className="form-control"
                                value={formData.email}
                                onChange={handleChange}
                                readOnly={emailReadOnly}
                                required
                                style={emailReadOnly ? {backgroundColor: '#f8f9fa'} : {}}
                            />
                            {invitationToken && !emailReadOnly && (
                                <small className="form-text text-muted">
                                    {t('auth.register.emailSameAsInvitation')}
                                </small>
                            )}
                            {emailReadOnly && (
                                <small className="form-text text-muted">
                                    {t('auth.register.emailLinkedToInvitation')}
                                </small>
                            )}
                        </div>

                        {/* Password */}
                        <div className="mb-3">
                            <label htmlFor="password" className="form-label">{t('auth.register.password')}</label>
                            <input
                                type="password"
                                id="password"
                                name="password"
                                className="form-control"
                                value={formData.password}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        {/* Display Name (Optional) */}
                        <div className="mb-3">
                            <label htmlFor="display_name" className="form-label">{t('auth.register.displayName')}</label>
                            <input
                                type="text"
                                id="display_name"
                                name="display_name"
                                className="form-control"
                                value={formData.display_name}
                                onChange={handleChange}
                                placeholder={t('auth.register.displayNamePlaceholder')}
                            />
                            <small className="form-text text-muted">
                                {t('auth.register.displayNameHint')}
                            </small>
                        </div>

                        {/* Terms and Privacy Policy Agreement */}
                        <div className="mb-3 form-check">
                            <input
                                type="checkbox"
                                className="form-check-input"
                                id="termsCheckbox"
                                checked={termsAccepted}
                                onChange={(e) => setTermsAccepted(e.target.checked)}
                                required
                            />
                            <label className="form-check-label" htmlFor="termsCheckbox">
                                {t('auth.register.termsAgree')} <Link to="/terms" target="_blank">{t('auth.register.termsOfService')}</Link> {t('auth.register.and')}{' '}
                                <Link to="/privacy" target="_blank">{t('auth.register.privacyPolicy')}</Link>
                            </label>
                        </div>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            className="btn btn-primary w-100"
                            disabled={loading}
                        >
                            {loading ? t('auth.register.registering') : t('auth.register.registerButton')}
                        </button>
                    </form>

                    {/* Messages */}
                    {errorMessage && (
                        <div className="alert alert-danger mt-3" role="alert">
                            {errorMessage}
                        </div>
                    )}
                    {successMessage && (
                        <div className="alert alert-success mt-3" role="alert">
                            {successMessage}
                            {isInvitedRegistration && (
                                <div className="mt-2">
                                    <div className="spinner-border spinner-border-sm me-2" role="status">
                                        <span className="visually-hidden">{t('auth.register.redirecting')}</span>
                                    </div>
                                    <span>{t('auth.register.redirectingToLogin')}</span>
                                </div>
                            )}
                        </div>
                    )}

                    <div className="mt-3 text-center">
                        <p>{t('auth.register.haveAccount')} <Link to="/login">{t('auth.register.loginHere')}</Link></p>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default RegisterForm;